import React from 'react';
import { CopyCard, CopyField, setStoreCopy } from './adminSiteCopyFields';
import AdminSiteGiftFinderFields from './AdminSiteGiftFinderFields';

export default function AdminSiteShopFields({
  form,
  setField,
  catalogCategories = [],
  catalogProducts = {},
  setStatus,
}) {
  const store = form.store_copy || {};
  const features = Array.isArray(store.shop_features) ? store.shop_features : [{}, {}, {}];
  const bullets = Array.isArray(store.pdp_bullets) ? store.pdp_bullets : [{}, {}, {}];

  const setStore = (patch) => setStoreCopy(form, setField, patch);

  const setFeature = (index, patch) => {
    const next = [0, 1, 2].map((i) => ({ ...(features[i] || {}), ...(i === index ? patch : {}) }));
    setStore({ shop_features: next });
  };

  const setBullet = (index, patch) => {
    const next = [0, 1, 2].map((i) => ({ ...(bullets[i] || {}), ...(i === index ? patch : {}) }));
    setStore({ pdp_bullets: next });
  };

  return (
    <>
      <AdminSiteGiftFinderFields
        form={form}
        setField={setField}
        catalogCategories={catalogCategories}
        catalogProducts={catalogProducts}
        setStatus={setStatus}
      />

      <CopyCard title="Shop page hero" subtitle="/shop landing page">
        <CopyField id="shop-eyebrow" label="Eyebrow" value={store.shop_eyebrow || ''} onChange={(v) => setStore({ shop_eyebrow: v })} />
        <CopyField id="shop-title" label="Headline" value={store.shop_title || ''} onChange={(v) => setStore({ shop_title: v })} />
        <CopyField id="shop-subtitle" label="Subtitle" value={store.shop_subtitle || ''} onChange={(v) => setStore({ shop_subtitle: v })} multiline={3} />
        <CopyField id="shop-section-heading" label="Categories section heading" value={store.shop_section_heading || ''} onChange={(v) => setStore({ shop_section_heading: v })} />
        <CopyField id="shop-section-lead" label="Categories section lead" value={store.shop_section_lead || ''} onChange={(v) => setStore({ shop_section_lead: v })} multiline={2} />
      </CopyCard>

      <CopyCard title="Shop feature cards" subtitle="Three cards below categories">
        {[0, 1, 2].map((i) => (
          <div key={`feat-${i}`} className="col-12 border rounded p-3 bg-light">
            <p className="small fw-semibold mb-2">Card {i + 1}</p>
            <CopyField id={`shop-feat-icon-${i}`} label="Icon (emoji)" value={features[i]?.icon || ''} onChange={(v) => setFeature(i, { icon: v })} />
            <CopyField id={`shop-feat-title-${i}`} label="Title" value={features[i]?.title || ''} onChange={(v) => setFeature(i, { title: v })} />
            <CopyField id={`shop-feat-desc-${i}`} label="Description" value={features[i]?.desc || ''} onChange={(v) => setFeature(i, { desc: v })} multiline={2} />
          </div>
        ))}
      </CopyCard>

      <CopyCard title="Product page trust lines" subtitle="Bullets under price on /product/…">
        <CopyField id="pdp-about-heading" label="Description heading" value={store.pdp_about_heading || ''} onChange={(v) => setStore({ pdp_about_heading: v })} />
        {[0, 1, 2].map((i) => (
          <div key={`pdp-bullet-${i}`} className="col-12 border rounded p-3 bg-light">
            <p className="small fw-semibold mb-2">Bullet {i + 1}</p>
            <CopyField id={`pdp-bullet-text-${i}`} label="Text" value={bullets[i]?.text || ''} onChange={(v) => setBullet(i, { text: v })} />
            <CopyField id={`pdp-bullet-link-${i}`} label="Optional link label" value={bullets[i]?.link_label || ''} onChange={(v) => setBullet(i, { link_label: v })} hint="e.g. shipping — leave blank for plain text only." />
            <CopyField id={`pdp-bullet-href-${i}`} label="Optional link URL" value={bullets[i]?.link_href || ''} onChange={(v) => setBullet(i, { link_href: v })} />
          </div>
        ))}
        <CopyField
          id="whatsapp-product-template"
          label="WhatsApp message template"
          value={store.whatsapp_product_template || ''}
          onChange={(v) => setStore({ whatsapp_product_template: v })}
          hint="Use {title} and {id} placeholders."
          multiline={2}
        />
      </CopyCard>

      <CopyCard title="Collection hub fallbacks" subtitle="When category hero tagline is blank in catalog">
        <CopyField id="tagline-rakhi" label="Rakhi collections fallback" value={store.category_tagline_rakhi || ''} onChange={(v) => setStore({ category_tagline_rakhi: v })} />
        <CopyField id="tagline-lumba" label="Lumba collections fallback" value={store.category_tagline_lumba || ''} onChange={(v) => setStore({ category_tagline_lumba: v })} />
      </CopyCard>

      <CopyCard title="Cart & bag page" subtitle="/cart">
        <CopyField id="cart-title" label="Page title" value={store.cart_title || ''} onChange={(v) => setStore({ cart_title: v })} />
        <CopyField id="cart-nonempty" label="Subtitle (items in bag)" value={store.cart_nonempty_lead || ''} onChange={(v) => setStore({ cart_nonempty_lead: v })} multiline={2} />
        <CopyField id="cart-empty-title" label="Empty bag title" value={store.cart_empty_title || ''} onChange={(v) => setStore({ cart_empty_title: v })} />
        <CopyField id="cart-empty-default" label="Empty bag message (default)" value={store.cart_empty_lead_default || ''} onChange={(v) => setStore({ cart_empty_lead_default: v })} multiline={3} />
        <CopyField id="cart-empty-stock" label="Empty bag message (stock changed)" value={store.cart_empty_lead_stock || ''} onChange={(v) => setStore({ cart_empty_lead_stock: v })} multiline={3} />
        <CopyField id="cart-empty-checkout" label="Empty bag message (after checkout)" value={store.cart_empty_lead_checkout || ''} onChange={(v) => setStore({ cart_empty_lead_checkout: v })} multiline={3} />
      </CopyCard>

      <CopyCard title="Order success & 404" subtitle="Confirmation and not-found pages">
        <CopyField id="order-success-paid" label="Paid order message" value={store.order_success_paid_message || ''} onChange={(v) => setStore({ order_success_paid_message: v })} multiline={2} />
        <CopyField id="notfound-title" label="404 title" value={store.notfound_title || ''} onChange={(v) => setStore({ notfound_title: v })} />
        <CopyField id="notfound-lead" label="404 message" value={store.notfound_lead || ''} onChange={(v) => setStore({ notfound_lead: v })} multiline={3} />
        <CopyField id="notfound-shop-btn" label="404 shop button" value={store.notfound_shop_button || ''} onChange={(v) => setStore({ notfound_shop_button: v })} />
        <CopyField id="notfound-home-btn" label="404 home button" value={store.notfound_home_button || ''} onChange={(v) => setStore({ notfound_home_button: v })} />
      </CopyCard>

      <CopyCard title="Checkout trust badges & footer">
        <CopyField id="trust-1" label="Trust badge 1" value={store.trust_badge_1 || ''} onChange={(v) => setStore({ trust_badge_1: v })} />
        <CopyField id="trust-2" label="Trust badge 2" value={store.trust_badge_2 || ''} onChange={(v) => setStore({ trust_badge_2: v })} />
        <CopyField id="trust-3" label="Trust badge 3" value={store.trust_badge_3 || ''} onChange={(v) => setStore({ trust_badge_3: v })} />
        <CopyField id="footer-made-in" label="Footer “made in” line" value={store.footer_made_in || ''} onChange={(v) => setStore({ footer_made_in: v })} hint="Shown in the site footer bar." />
      </CopyCard>

      <CopyCard title="Other pages — SEO titles" subtitle="Browser titles for cart, checkout, wishlist, etc.">
        <CopyField id="seo-cart-title" label="Cart page title" value={store.seo_cart_title || ''} onChange={(v) => setStore({ seo_cart_title: v })} />
        <CopyField id="seo-cart-desc" label="Cart meta description" value={store.seo_cart_description || ''} onChange={(v) => setStore({ seo_cart_description: v })} multiline={2} />
        <CopyField id="seo-checkout-title" label="Checkout page title" value={store.seo_checkout_title || ''} onChange={(v) => setStore({ seo_checkout_title: v })} />
        <CopyField id="seo-checkout-desc" label="Checkout meta description" value={store.seo_checkout_description || ''} onChange={(v) => setStore({ seo_checkout_description: v })} multiline={2} />
        <CopyField id="seo-wishlist-title" label="Wishlist page title" value={store.seo_wishlist_title || ''} onChange={(v) => setStore({ seo_wishlist_title: v })} />
        <CopyField id="seo-wishlist-desc" label="Wishlist meta description" value={store.seo_wishlist_description || ''} onChange={(v) => setStore({ seo_wishlist_description: v })} multiline={2} />
        <CopyField id="seo-order-success-title" label="Order success page title" value={store.seo_order_success_title || ''} onChange={(v) => setStore({ seo_order_success_title: v })} />
        <CopyField id="seo-notfound-title" label="404 page title" value={store.seo_notfound_title || ''} onChange={(v) => setStore({ seo_notfound_title: v })} />
      </CopyCard>
    </>
  );
}
